/**
 * Rate Limit Service
 *
 * Database-backed rate limiting for login attempts.
 * Replaces in-memory Map with persistent storage for:
 * - Persistence across server restarts
 * - Consistency across multiple browser tabs/windows
 * - Centralized tracking for security monitoring
 *
 * Created: January 25, 2026
 * Related: 20260125000100_database_rate_limiting.sql
 */

import { supabase } from "./supabaseClient";
import { logger } from "../lib/logger";

// ============================================
// TYPES
// ============================================

export interface RateLimitStatus {
    /** Whether the user can attempt login */
    canAttempt: boolean;
    /** Current attempt count in window */
    attemptCount: number;
    /** Remaining attempts before lockout */
    remainingAttempts: number;
    /** Minutes until lockout expires (if locked) */
    remainingMinutes: number;
    /** Whether the user is approaching the limit (warning state) */
    isWarning: boolean;
    /** Whether the user is currently locked out */
    isLocked: boolean;
}

export interface RecordAttemptResult {
    /** Whether the user is now locked out */
    isLocked: boolean;
    /** Updated attempt count */
    attemptCount: number;
    /** Minutes remaining in lockout (if locked) */
    remainingMinutes: number;
    /** Whether this is a warning (approaching limit) */
    isWarning: boolean;
}

// ============================================
// CONFIGURATION
// ============================================

/** Maximum login attempts before lockout */
const MAX_ATTEMPTS = 5;

/** Cache TTL in milliseconds (reduce database calls) */
const CACHE_TTL_MS = 5000; // 5 seconds

/** Rate limit configuration for UI display */
export const RATE_LIMIT_CONFIG = {
    maxAttempts: MAX_ATTEMPTS,
    windowMinutes: 15,
    lockoutMinutes: 15,
} as const;

// ============================================
// LOCAL CACHE (for performance)
// ============================================

interface CachedStatus {
    status: RateLimitStatus;
    timestamp: number;
}

const statusCache = new Map<string, CachedStatus>();

/**
 * Clear cached status for an email
 */
const clearCache = (email: string): void => {
    statusCache.delete(email.toLowerCase());
};

/**
 * Get cached status if still valid
 */
const getCachedStatus = (email: string): RateLimitStatus | null => {
    const key = email.toLowerCase();
    const cached = statusCache.get(key);

    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return cached.status;
    }

    return null;
};

/**
 * Set cached status
 */
const setCachedStatus = (email: string, status: RateLimitStatus): void => {
    statusCache.set(email.toLowerCase(), {
        status,
        timestamp: Date.now(),
    });
};

// ============================================
// SERVICE FUNCTIONS
// ============================================

/**
 * Check rate limit status for an email
 *
 * @param email - The email to check
 * @returns RateLimitStatus with detailed info
 */
export const checkRateLimit = async (email: string): Promise<RateLimitStatus> => {
    const normalizedEmail = email.toLowerCase().trim();

    // Check cache first
    const cached = getCachedStatus(normalizedEmail);
    if (cached) {
        logger.debug('Rate limit cache hit', { email: normalizedEmail });
        return cached;
    }

    try {
        const { data, error } = await supabase.rpc('check_login_rate_limit', {
            p_email: normalizedEmail,
        });

        if (error) {
            logger.error('Rate limit check failed', { error: error.message, email: normalizedEmail });
            // On error, allow attempt (fail open for availability)
            return {
                canAttempt: true,
                attemptCount: 0,
                remainingAttempts: MAX_ATTEMPTS,
                remainingMinutes: 0,
                isWarning: false,
                isLocked: false,
            };
        }

        // Handle the RPC result (returns array with single row)
        const result = Array.isArray(data) ? data[0] : data;

        const status: RateLimitStatus = {
            canAttempt: result?.can_attempt ?? true,
            attemptCount: result?.attempt_count ?? 0,
            remainingAttempts: Math.max(0, MAX_ATTEMPTS - (result?.attempt_count ?? 0)),
            remainingMinutes: result?.remaining_minutes ?? 0,
            isWarning: result?.is_warning ?? false,
            isLocked: result?.is_locked ?? false,
        };

        // Cache the result
        setCachedStatus(normalizedEmail, status);

        return status;
    } catch (error: unknown) {
        logger.error('Rate limit check exception', { error, email: normalizedEmail });
        // Fail open
        return {
            canAttempt: true,
            attemptCount: 0,
            remainingAttempts: MAX_ATTEMPTS,
            remainingMinutes: 0,
            isWarning: false,
            isLocked: false,
        };
    }
};

/**
 * Record a login attempt
 *
 * @param email - The email to record attempt for
 * @returns RecordAttemptResult with updated status
 */
export const recordAttempt = async (email: string): Promise<RecordAttemptResult> => {
    const normalizedEmail = email.toLowerCase().trim();

    // Clear cache since we're modifying state
    clearCache(normalizedEmail);

    try {
        const { data, error } = await supabase.rpc('record_login_attempt', {
            p_email: normalizedEmail,
        });

        if (error) {
            logger.error('Record attempt failed', { error: error.message, email: normalizedEmail });
            // Return safe default (not locked)
            return {
                isLocked: false,
                attemptCount: 1,
                remainingMinutes: 0,
                isWarning: false,
            };
        }

        // Handle the RPC result
        const result = Array.isArray(data) ? data[0] : data;

        const attemptResult: RecordAttemptResult = {
            isLocked: result?.is_locked ?? false,
            attemptCount: result?.attempt_count ?? 1,
            remainingMinutes: result?.remaining_minutes ?? 0,
            isWarning: result?.is_warning ?? false,
        };

        if (attemptResult.isLocked) {
            logger.warn('User locked out due to rate limiting', {
                email: normalizedEmail,
                attemptCount: attemptResult.attemptCount,
                remainingMinutes: attemptResult.remainingMinutes,
            });
        } else if (attemptResult.isWarning) {
            logger.info('User approaching rate limit', {
                email: normalizedEmail,
                attemptCount: attemptResult.attemptCount,
                remaining: MAX_ATTEMPTS - attemptResult.attemptCount,
            });
        }

        return attemptResult;
    } catch (error: unknown) {
        logger.error('Record attempt exception', { error, email: normalizedEmail });
        return {
            isLocked: false,
            attemptCount: 1,
            remainingMinutes: 0,
            isWarning: false,
        };
    }
};

/**
 * Clear rate limit for an email (on successful login or admin action)
 *
 * @param email - The email to clear rate limit for
 * @returns Whether the clear was successful
 */
export const clearRateLimit = async (email: string): Promise<boolean> => {
    const normalizedEmail = email.toLowerCase().trim();

    // Clear cache
    clearCache(normalizedEmail);

    try {
        const { data, error } = await supabase.rpc('clear_login_rate_limit', {
            p_email: normalizedEmail,
        });

        if (error) {
            logger.error('Clear rate limit failed', { error: error.message, email: normalizedEmail });
            return false;
        }

        logger.debug('Rate limit cleared', { email: normalizedEmail, existed: data });
        return true;
    } catch (error: unknown) {
        logger.error('Clear rate limit exception', { error, email: normalizedEmail });
        return false;
    }
};

/**
 * Get all rate limited emails (admin function)
 *
 * @returns Array of rate limited emails with their status
 */
export const getAllRateLimitedEmails = async (): Promise<Array<{
    email: string;
    attemptCount: number;
    remainingMinutes: number;
    isLocked: boolean;
    firstAttemptAt: Date;
}>> => {
    try {
        const { data, error } = await supabase.rpc('get_rate_limited_emails');

        if (error) {
            logger.error('Get rate limited emails failed', { error: error.message });
            return [];
        }

        return (data || []).map((row: {
            email: string;
            attempt_count: number;
            remaining_minutes: number;
            is_locked: boolean;
            first_attempt_at: string;
        }) => ({
            email: row.email,
            attemptCount: row.attempt_count,
            remainingMinutes: row.remaining_minutes,
            isLocked: row.is_locked,
            firstAttemptAt: new Date(row.first_attempt_at),
        }));
    } catch (error: unknown) {
        logger.error('Get rate limited emails exception', { error });
        return [];
    }
};

/**
 * Reset cache for testing purposes only.
 * @internal - exported for testing only, not part of the public API.
 * Used by rateLimitService.test.ts to clear internal cache between tests.
 */
export const __resetCacheForTesting = (): void => {
    statusCache.clear();
};

/**
 * Rate limit service object for convenient importing
 */
export const rateLimitService = {
    checkRateLimit,
    recordAttempt,
    clearRateLimit,
    getAllRateLimitedEmails,
    RATE_LIMIT_CONFIG,
    __resetCacheForTesting,
};

export default rateLimitService;
